import { signal } from "@preact/signals";
import { applyOrderEvent, type EventEnvelope, type Order, type VenueId } from "@orderman/shared";
import { kvGet, kvSet } from "./idb.js";

/**
 * Cloud-Event-Mirror für den Mobilfunk-Betrieb: zieht die rohen Venue-Events aus dem Relay
 * (GET /events.php, read-only-Token) und projiziert sie LOKAL zu Orders — mit derselben
 * shared-Projektion wie der Server. So sieht ein Kellner ohne LAN trotzdem Bon-Status,
 * Bon-Nummer (order.numbered), verkaufte Mengen und Wartezeiten.
 *
 * Zustand ist per Venue in IndexedDB persistiert (Cursor + projizierte Orders), damit ein
 * Kaltstart nicht das ganze Log neu ziehen muss. Der Cursor ist der Cloud-`server_seq`.
 */
export const mirrorOrders = signal<Order[]>([]);

let cursor = "0";
let byId = new Map<string, Order>();
let loadedVenue: VenueId | null = null;

function key(venueId: VenueId): string {
  return `mirror:${venueId}`;
}

/** Persistierten Stand (Cursor + Orders) für die Venue laden. Idempotent je Venue. */
export async function initMirror(venueId: VenueId): Promise<void> {
  if (loadedVenue === venueId) return;
  const saved = await kvGet<{ cursor: string; orders: Order[] }>(key(venueId));
  cursor = saved?.cursor ?? "0";
  byId = new Map((saved?.orders ?? []).map((o) => [o.id, o]));
  loadedVenue = venueId;
  mirrorOrders.value = saved?.orders ?? [];
}

/** Bei Venue-Wechsel den Mirror leeren (kein Fremd-Stand). */
export async function resetMirror(venueId: VenueId): Promise<void> {
  cursor = "0";
  byId = new Map();
  loadedVenue = venueId;
  mirrorOrders.value = [];
  await kvSet(key(venueId), { cursor, orders: [] });
}

/** Aktueller Cloud-Cursor (für den Abgleich mit sync-status.eventCursor → nur bei Bedarf ziehen). */
export function getMirrorCursor(): string {
  return cursor;
}

/**
 * Neue Events aus dem Relay nachziehen (paginiert ab Cursor) und projizieren. Still bei
 * Fehlern (401/offline) — der bestehende Stand bleibt gültig. Gibt zurück, ob etwas Neues kam.
 */
export async function pullMirror(url: string, readKey: string, venueId: VenueId): Promise<boolean> {
  await initMirror(venueId);
  const limit = 500;
  let changed = false;
  for (;;) {
    let data: { events: EventEnvelope[]; nextCursor: string };
    try {
      const res = await fetch(
        `${url}/events.php?venue=${encodeURIComponent(venueId)}&since=${encodeURIComponent(cursor)}&limit=${limit}`,
        { headers: { "X-Api-Key": readKey } },
      );
      if (!res.ok) return changed;
      data = (await res.json()) as { events: EventEnvelope[]; nextCursor: string };
    } catch {
      return changed;
    }
    if (!data.events || data.events.length === 0) break;
    for (const e of data.events) {
      if (e.body.kind === "config.snapshot") continue; // Konfig läuft über bootstrap, nicht die Order-Projektion
      byId.set(e.body.orderId, applyOrderEvent(byId.get(e.body.orderId) ?? null, e));
    }
    cursor = data.nextCursor;
    changed = true;
    if (data.events.length < limit) break;
  }
  if (changed) {
    const orders = [...byId.values()];
    mirrorOrders.value = orders;
    await kvSet(key(venueId), { cursor, orders });
  }
  return changed;
}
